import { Injectable } from '@angular/core';
import {
  PublicClientApplication,
  RedirectRequest,
  AuthenticationResult
} from '@azure/msal-browser';
import type { AuthProvider } from './authentication.service';


const MSAL_CONFIG_KEY = 'msal_auth_config';
const MSAL_SCOPES_KEY = 'msal_auth_scopes';

interface MsalStoredConfig {
  clientId: string;
  authority: string;
}

@Injectable({ providedIn: 'root' })
export class MicrosoftAuthService {
  private msalInstance: PublicClientApplication | null = null;
  private initialized = false;

  private buildInstance(config: MsalStoredConfig): void {
    const baseHref = document.querySelector('base')?.getAttribute('href') ?? '/';
    const base = baseHref.endsWith('/') ? baseHref.slice(0, -1) : baseHref;
    const redirectUri = `${window.location.origin}${base}/oauth/callback`;
    this.msalInstance = new PublicClientApplication({
      auth: {
        clientId: config.clientId,
        authority: config.authority,
        redirectUri,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
    });
    this.initialized = false;
  }

  initFromProvider(provider: AuthProvider): void {
    const clientId = provider.clientId || ' 085ee7d4-34ba-4c70-8894-aed89e685506';
    const authority = provider.authority || 'https://login.microsoftonline.com/9e773c77-fa34-42c0-9052-74a094ee0edc';

    const cfg: MsalStoredConfig = { clientId, authority };
    localStorage.setItem(MSAL_CONFIG_KEY, JSON.stringify(cfg));

    this.buildInstance(cfg);
  }

  private async ensureInitialized(): Promise<boolean> {
    if (!this.msalInstance) {
      const raw = localStorage.getItem(MSAL_CONFIG_KEY);
      if (!raw) {
        return false;
      }
      try {
        const cfg = JSON.parse(raw) as MsalStoredConfig;
        this.buildInstance(cfg);
      } catch (e) {
        console.error('Erreur en reconstruisant la config MSAL depuis le stockage:', e);
        return false;
      }
    }

    if (!this.initialized) {
      await this.msalInstance!.initialize();
      this.initialized = true;
    }

    return true;
  }

  loginRedirect(scopes: string[] = ['User.Read']): void {
    localStorage.setItem( MSAL_SCOPES_KEY, JSON.stringify(scopes));
    const doLogin = async () => {
      const ok = await this.ensureInitialized();
      if (!ok) {
        throw new Error('MSAL non initialisé (pas de config)');
      }

      const request: RedirectRequest = { scopes };
      await this.msalInstance!.loginRedirect(request);
    };

    void doLogin();
  }

  async handleRedirectCallback(): Promise<AuthenticationResult | null> {
    const ok = await this.ensureInitialized();
    if (!ok) {
      return null;
    }

    const result = await this.msalInstance!.handleRedirectPromise();
     if (result?.account) {
      this.msalInstance!.setActiveAccount(
        result.account
      );
    }
    return result ?? null;
  }

  async acquireTokenSilent(forceRefresh = false): Promise<AuthenticationResult> {
    const ok = await this.ensureInitialized();
    if (!ok) {
      throw new Error('MSAL non initialisé');
    }
    const account =
      this.msalInstance!.getActiveAccount() ??
      this.msalInstance!.getAllAccounts()[0];
    if (!account) {
      throw new Error('Aucun compte Microsoft disponible');
    }
    let scopes: string[] = ['User.Read'];
    const storedScopes = localStorage.getItem(MSAL_SCOPES_KEY);
    if (storedScopes) {
      try {
        const parsedScopes = JSON.parse(storedScopes);
        if (Array.isArray(parsedScopes)) {
          scopes = parsedScopes;
        }
      } catch {
      }
    }
    return this.msalInstance!.acquireTokenSilent({
      account,
      scopes,
      forceRefresh
    });
  }
}
